/**
 * Cryptographically secure random password generator.
 *
 * Uses `crypto.getRandomValues()` (Web Crypto API) instead of `Math.random()`,
 * which is NOT suitable for security-sensitive tokens. Used for every
 * auto-generated credential the portal hands to a user — account members,
 * sub-accounts, team owners, Revue users and share links.
 */

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS = '0123456789';
const SPECIAL = '!@#$%&*';

const DEFAULT_CHARS = LOWER + UPPER + DIGITS + '!@#$%';

/**
 * Character set with the visually ambiguous glyphs removed (I/l/1, O/o/0).
 * For credentials a person reads off a screen and re-types — share links.
 */
export const UNAMBIGUOUS_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

/**
 * Uniform random integer in `[0, maxExclusive)` from the CSPRNG.
 *
 * Rejection-samples values in the uneven remainder bucket so every result has
 * equal probability. Plain `v % max` would bias toward the low end whenever
 * `max` does not divide the `Uint32` range evenly.
 */
function randomInt(maxExclusive: number): number {
  const threshold = Math.floor(0xffffffff / maxExclusive) * maxExclusive;
  const buf = new Uint32Array(1);
  let v: number;
  do {
    crypto.getRandomValues(buf);
    v = buf[0];
  } while (v >= threshold);
  return v % maxExclusive;
}

/** Fisher-Yates, driven by the CSPRNG rather than `Math.random()`. */
function shuffle(items: string[]): string[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}

/**
 * Random password drawn uniformly from `chars`.
 *
 * Makes no guarantee about which character classes appear. Use it where the
 * consumer has no complexity rule — e.g. share-link passwords with
 * {@link UNAMBIGUOUS_CHARS}. For a credential that must satisfy a complexity
 * check, use {@link generateComplexPassword}.
 */
export function generateSecurePassword(length = 12, chars = DEFAULT_CHARS): string {
  let out = '';
  for (let i = 0; i < length; i++) {
    out += chars[randomInt(chars.length)];
  }
  return out;
}

/**
 * Random password guaranteed to contain at least one lowercase letter,
 * uppercase letter, digit and symbol.
 *
 * This is what every account credential needs: `validatePasswordComplexity`
 * (and scio1's `validatePasswordNew` before it) rejects a password missing any
 * class once `company.feature.enforcePasswordComplexity` is set. Drawing
 * uniformly is not enough — a 12-character uniform draw from the 67-character
 * default set omits a symbol ~39% of the time and a digit ~14% of the time,
 * so a generated password would regularly fail the very form that produced it.
 *
 * @param length total length; must be at least 4 to fit one of each class.
 */
export function generateComplexPassword(length = 12): string {
  const classes = [LOWER, UPPER, DIGITS, SPECIAL];
  if (length < classes.length) {
    throw new Error(`generateComplexPassword: length must be >= ${classes.length}`);
  }
  const all = classes.join('');
  const chars = classes.map((set) => set[randomInt(set.length)]);
  for (let i = chars.length; i < length; i++) {
    chars.push(all[randomInt(all.length)]);
  }
  // Without the shuffle the first four positions would always be
  // lower/upper/digit/symbol in order, which is a predictable structure.
  return shuffle(chars).join('');
}
